import { Edit, Delete } from '@mui/icons-material';
import { IconButton, Box, Chip } from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { memo, useMemo, useCallback } from 'react';
import type { Vessel } from '@/types';

interface VesselsDataGridProps {
  vessels: Vessel[];
  loading?: boolean;
  onEdit?: (vessel: Vessel) => void;
  onDelete?: (vessel: Vessel, event?: React.MouseEvent<HTMLElement>) => void;
}

export const VesselsDataGrid = memo(
  ({ vessels, loading = false, onEdit, onDelete }: VesselsDataGridProps) => {
    const handleEdit = useCallback(
      (vessel: Vessel) => {
        onEdit?.(vessel);
      },
      [onEdit]
    );

    const handleDelete = useCallback(
      (vessel: Vessel, event?: React.MouseEvent<HTMLElement>) => {
        onDelete?.(vessel, event);
      },
      [onDelete]
    );

    const columns: GridColDef[] = useMemo(
      () => [
        {
          field: 'VesselName',
          headerName: 'Vessel Name',
          width: 200,
          flex: 1,
        },
        {
          field: 'IMO',
          headerName: 'IMO',
          width: 150,
        },
        {
          field: 'FleetManager',
          headerName: 'Fleet Manager',
          width: 200,
          flex: 1,
        },
        {
          field: 'RegistrationStatus',
          headerName: 'Status',
          width: 120,
          renderCell: (params: GridRenderCellParams) => (
            <Chip
              label={params.value}
              color={params.value === 'ACTIVE' ? 'success' : 'default'}
              size="small"
              variant="outlined"
              sx={{
                fontSize: '0.75rem',
                fontWeight: 500,
                height: 24,
              }}
            />
          ),
        },
        {
          field: 'TotalCO2InTons',
          headerName: 'Total CO2 (tons)',
          width: 150,
          type: 'number',
          valueFormatter: (value: number) => value?.toFixed(2) || '0.00',
        },
        {
          field: 'UpdatedAt',
          headerName: 'Last Updated',
          width: 180,
          type: 'dateTime',
          valueGetter: (params) => new Date(params),
        },
        {
          field: 'actions',
          headerName: 'Actions',
          width: 120,
          sortable: false,
          filterable: false,
          renderCell: (params: GridRenderCellParams<Vessel>) => (
            <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', height: '100%' }}>
              {onEdit && (
                <IconButton size="small" onClick={() => handleEdit(params.row)} color="primary">
                  <Edit fontSize="small" />
                </IconButton>
              )}
              {onDelete && (
                <IconButton size="small" onClick={(e) => handleDelete(params.row, e)} color="error">
                  <Delete fontSize="small" />
                </IconButton>
              )}
            </Box>
          ),
        },
      ],
      [onEdit, onDelete, handleEdit, handleDelete]
    );

    const initialState = useMemo(
      () => ({
        pagination: {
          paginationModel: { pageSize: 25 },
        },
        sorting: {
          sortModel: [{ field: 'UpdatedAt', sort: 'desc' as const }],
        },
      }),
      []
    );

    return (
      <Box sx={{ width: '100%' }}>
        <DataGrid
          rows={vessels}
          columns={columns}
          getRowId={(row) => row.ID}
          loading={loading}
          pageSizeOptions={[10, 25, 50, 100]}
          initialState={initialState}
          disableRowSelectionOnClick
          getRowClassName={(params) =>
            params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
          }
          sx={{
            height: 600,
            width: '100%',
          }}
        />
      </Box>
    );
  }
);
